'use client';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';

import { inventoryGridClassName } from '@/components/Inventory/InventoryGrid';
import { InventoryItem } from '@/components/Inventory/InventoryItem';
import { InventoryInfiniteScrollContext } from '@/contexts/InventoryInfiniteScrollContext';
import { NewInventoryFiltersContext } from '@/contexts/NewInventoryFiltersContext';
import { getItems } from '@/lib/actions';
import { convertFilterSection, convertSortBy } from '@/tools/inventory';
import { FilterSection } from '@/types/inventory';
import { faChevronUp } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Grid, LaMesaRecVanLogo } from '@lamesarv-sdk/components';
import { IBasicInventory, IComponentSearchResults, InventoryField } from '@lamesarv-sdk/types';

import { MobileTabletHeader } from './MobileTabletHeader';

const ScrollToTop = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const toggleVisibility = () => {
      setIsVisible(window.scrollY > 300);
    };

    window.addEventListener('scroll', toggleVisibility);
    return () => {
      window.removeEventListener('scroll', toggleVisibility);
    };
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <button
      type="button"
      className={`fixed bottom-5 right-5 bg-neutral-900 text-white p-2.5 pb-1.5 z-40 rounded-md ${isVisible ? 'block' : 'hidden'} md:hidden`}
      onClick={scrollToTop}
    >
      <FontAwesomeIcon icon={faChevronUp} className="w-5 h-5" />
    </button>
  );
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const SearchResults = (_props: IComponentSearchResults) => {
  const { filters, baseFilters } = useContext(NewInventoryFiltersContext);
  const { infiniteScrollPageRef, savedItemsRef, savedMetadataRef } = useContext(InventoryInfiniteScrollContext);
  const [items, setItems] = useState<IBasicInventory[]>(savedItemsRef.current || []);
  const [total, setTotal] = useState<number | null>(savedMetadataRef.current?.nbHits || null);
  const [ref, inView] = useInView({
    rootMargin: '0px 0px 50% 0px',
  });

  const wrappedGetItems = useCallback(
    (currentPage?: number) => {
      const finalFilters = convertFilterSection(filters, baseFilters);
      const sort = convertSortBy(filters[FilterSection.SORT_BY]);

      return getItems({
        filters: finalFilters,
        sort,
        currentPage,
      });
    },
    [filters, baseFilters],
  );

  useEffect(() => {
    const controller = new AbortController();

    setItems([]);
    setTotal(null);
    wrappedGetItems().then((result) => {
      if (controller.signal.aborted) return;

      setItems(result.items);
      setTotal(result.total);
    });

    return () => {
      controller.abort();
    };
  }, [wrappedGetItems]);

  const getMoreItems = async () => {
    infiniteScrollPageRef.current++;

    const itemsResult = await wrappedGetItems(infiniteScrollPageRef.current);

    setItems((prevItems) => {
      const finalItems = [...prevItems, ...itemsResult.items];

      savedItemsRef.current = finalItems;

      return finalItems;
    });

    savedMetadataRef.current = {
      nbHits: itemsResult.total,
    };
    setTotal(itemsResult.total);
  };

  useEffect(() => {
    if (!inView) return;

    getMoreItems();
  }, [inView]);

  return (
    <>
      <MobileTabletHeader total={total} />

      <div className="max-lg:py-10">
        <Grid
          items={items.map((item) => (
            <InventoryItem key={item[InventoryField.id]} item={item} />
          ))}
          baseCols={1}
          smCols={1}
          mdCols={2}
          lgCols={2}
          xlCols={3}
          gap={6}
          className={inventoryGridClassName}
        />
      </div>

      {!!total && total > items.length && (
        <div ref={ref} className="flex flex-row justify-center mt-10 animate-pulse md:hidden">
          <LaMesaRecVanLogo className="h-20" />
        </div>
      )}

      <ScrollToTop />
    </>
  );
};
